import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, UserCog, Mail, Phone, Shield, Save, Bell, BellOff, KeyRound, Trash2, UserX, UserCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const ROLES = ['Administrator', 'Owner', 'Office Manager', 'Employee'];
const roleBadgeColor = {
  Administrator: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  Owner: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  'Office Manager': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Employee: 'bg-primary/10 text-primary border-primary/20',
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [perms, setPerms] = useState({});
  const [createMode, setCreateMode] = useState(false);
  const [form, setForm] = useState({});
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const isAdmin = user?.role === 'Administrator';

  useEffect(() => {
    if (isAdmin) {
      api.get('/users').then(r => setUsers((Array.isArray(r.data) ? r.data : []).filter(u => u.role !== 'Client')));
    }
  }, [isAdmin]);

  // Guard: only real Administrators can access this page
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="employees-access-denied">
        <div className="text-center space-y-2">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="text-lg font-semibold">Access Restricted</h2>
          <p className="text-sm text-muted-foreground">Only Administrators can manage employees.</p>
        </div>
      </div>
    );
  }

  const fetchUsers = () => api.get('/users').then(r => setUsers((Array.isArray(r.data) ? r.data : []).filter(u => u.role !== 'Client')));

  const openEdit = async (u) => {
    setSelectedUser(u);
    setCreateMode(false);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      hourly_rate: u.hourly_rate ?? 0,
      role: u.role || 'Employee',
      email_notifications_enabled: u.email_notifications_enabled !== false,
    });
    if (u.role === 'Employee' || u.role === 'Office Manager') {
      const r = await api.get(`/users/${u.id}/permissions`).catch(() => ({ data: {} }));
      setPerms(r.data || {});
    } else {
      setPerms({});
    }
    setSheetOpen(true);
  };

  const openCreate = () => {
    setCreateMode(true);
    setSelectedUser(null);
    setForm({ name: '', email: '', password: 'password123', role: 'Employee', phone: '', hourly_rate: 0 });
    setSheetOpen(true);
  };

  const handleCreate = async () => {
    try {
      if (!form.name || !form.email) return toast.error('Name and email required');
      await api.post('/auth/register', form);
      toast.success('Employee created');
      setSheetOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.name !== selectedUser.name) payload.name = editForm.name;
      if (editForm.email !== selectedUser.email) payload.email = editForm.email;
      if (editForm.phone !== (selectedUser.phone || '')) payload.phone = editForm.phone;
      if (editForm.hourly_rate !== (selectedUser.hourly_rate ?? 0)) payload.hourly_rate = parseFloat(editForm.hourly_rate) || 0;
      if (editForm.role !== selectedUser.role) payload.role = editForm.role;
      const prevNotif = selectedUser.email_notifications_enabled !== false;
      if (editForm.email_notifications_enabled !== prevNotif) payload.email_notifications_enabled = editForm.email_notifications_enabled;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }
      const res = await api.put(`/users/${selectedUser.id}`, payload);
      toast.success('Employee updated');
      setSelectedUser(res.data);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!resetPw || resetPw.length < 6) return toast.error('Password must be at least 6 characters');
    try {
      const res = await api.put(`/users/${selectedUser.id}/reset-password`, { new_password: resetPw });
      toast.success(res.data.status || 'Password reset');
      setResetPw('');
      setResetPwOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  const handleToggleActive = async () => {
    if (!selectedUser) return;
    if (selectedUser.id === user?.id) return toast.error("Cannot deactivate your own account");
    setToggling(true);
    try {
      const endpoint = selectedUser.is_active !== false ? 'deactivate' : 'reactivate';
      await api.put(`/users/${selectedUser.id}/${endpoint}`);
      const newStatus = endpoint === 'deactivate' ? false : true;
      toast.success(`Employee ${newStatus ? 'reactivated' : 'deactivated'}`);
      setSelectedUser({ ...selectedUser, is_active: newStatus });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setToggling(false);
  };

  const handleDeleteEmployee = async () => {
    if (!selectedUser) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success(`${selectedUser.name} has been deleted`);
      setDeleteConfirmOpen(false);
      setSheetOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    setDeleting(false);
  };

  const updatePerm = async (key, value) => {
    const updated = { ...perms, [key]: value };
    setPerms(updated);
    await api.put(`/users/${selectedUser.id}/permissions`, updated);
    toast.success('Permission updated');
  };

  const filtered = users.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()));

  const permToggles = [
    { key: 'approve_proposals', label: 'Approve Proposals' },
    { key: 'approve_timesheets', label: 'Approve Timesheets' },
    { key: 'approve_invoices', label: 'Approve Invoices' },
    { key: 'view_financials', label: 'View Financials' },
    { key: 'manage_clients', label: 'Manage Clients' },
    { key: 'manage_employees', label: 'Manage Employees' },
  ];

  const visibilityFields = [
    { key: 'projects_visibility', label: 'Projects' },
    { key: 'clients_visibility', label: 'Clients' },
    { key: 'invoices_visibility', label: 'Invoices' },
    { key: 'proposals_visibility', label: 'Proposals' },
  ];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-lg tracking-tight">Employees</h1>
          <p className="text-sm text-muted-foreground">{users.length} staff members</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid="create-employee-button">
          <Plus className="h-4 w-4 mr-1" /> New Employee
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search employees..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="employee-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employees-grid">
        {filtered.map(u => (
          <Card key={u.id} className="border-border bg-card shadow-none hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => openEdit(u)} data-testid={`employee-card-${u.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                  {u.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{u.name}</p>
                    <Badge className={`text-xs shrink-0 ${roleBadgeColor[u.role] || ''}`}>{u.role}</Badge>
                  </div>
                  <div className="mt-1.5 space-y-0.5">
                    {u.email && <p className="flex items-center gap-1 text-xs text-muted-foreground truncate"><Mail className="h-3 w-3 shrink-0" /> {u.email}</p>}
                    {u.phone && <p className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3 shrink-0" /> {u.phone}</p>}
                    {u.hourly_rate > 0 && <p className="text-xs text-primary font-medium">${u.hourly_rate}/hr</p>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {!u.is_active && <Badge variant="outline" className="text-xs text-destructive border-destructive/20">Inactive</Badge>}
                    {u.email_notifications_enabled === false && (
                      <Badge variant="outline" className="text-xs text-muted-foreground border-border gap-0.5">
                        <BellOff className="h-2.5 w-2.5" /> Emails Off
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto">
          {createMode ? (
            <>
              <SheetHeader><SheetTitle className="font-heading">New Employee</SheetTitle><SheetDescription>Create a new staff account</SheetDescription></SheetHeader>
              <div className="space-y-3 mt-4">
                <div><Label className="text-xs text-muted-foreground">Full Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="employee-form-name" /></div>
                <div><Label className="text-xs text-muted-foreground">Email *</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" data-testid="employee-form-email" /></div>
                <div><Label className="text-xs text-muted-foreground">Password</Label><Input value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-background border-border" data-testid="employee-form-password" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="employee-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" data-testid="employee-form-phone" /></div>
                <div><Label className="text-xs text-muted-foreground">Hourly Rate ($)</Label><Input type="number" step="0.01" min="0" value={form.hourly_rate || ''} onChange={e => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="employee-form-rate" /></div>
                <Button onClick={handleCreate} className="w-full bg-primary text-primary-foreground" data-testid="employee-form-submit">Create Employee</Button>
              </div>
            </>
          ) : selectedUser && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading flex items-center gap-2"><UserCog className="h-5 w-5 text-primary" /> Edit Employee</SheetTitle>
                <SheetDescription>Update {selectedUser.name}'s record</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                {/* Editable fields */}
                <div>
                  <Label className="text-xs text-muted-foreground">Full Name</Label>
                  <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-background border-border" data-testid="edit-employee-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="bg-background border-border" data-testid="edit-employee-email" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="bg-background border-border" data-testid="edit-employee-phone" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Hourly Rate ($)</Label>
                  <Input type="number" step="0.01" min="0" value={editForm.hourly_rate} onChange={e => setEditForm({ ...editForm, hourly_rate: parseFloat(e.target.value) || 0 })} className="bg-background border-border" placeholder="0.00" data-testid="edit-employee-rate" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Role</Label>
                  <Select value={editForm.role} onValueChange={v => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="edit-employee-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                {/* Email Notification Toggle */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {editForm.email_notifications_enabled ? (
                        <Bell className="h-4 w-4 text-primary" />
                      ) : (
                        <BellOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div>
                        <Label className="text-sm">Email Notifications</Label>
                        <p className="text-xs text-muted-foreground">Send portal email alerts to this user</p>
                      </div>
                    </div>
                    <Switch
                      checked={editForm.email_notifications_enabled}
                      onCheckedChange={v => setEditForm({ ...editForm, email_notifications_enabled: v })}
                      data-testid="edit-employee-email-toggle"
                    />
                  </div>
                </div>

                <Button onClick={handleSaveEdit} disabled={saving} className="w-full bg-primary text-primary-foreground gap-1.5" data-testid="edit-employee-save">
                  <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>

                {/* Reset Password */}
                <div className="border-t border-border pt-4">
                  {!resetPwOpen ? (
                    <Button variant="outline" size="sm" onClick={() => setResetPwOpen(true)} className="w-full border-border text-muted-foreground hover:text-foreground gap-1.5" data-testid="reset-password-toggle">
                      <KeyRound className="h-3.5 w-3.5" /> Reset Password
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">New Password for {selectedUser.name}</Label>
                      <Input
                        type="password"
                        value={resetPw}
                        onChange={e => setResetPw(e.target.value)}
                        placeholder="At least 6 characters"
                        className="bg-background border-border"
                        data-testid="admin-reset-password-input"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleResetPassword} className="bg-primary text-primary-foreground gap-1" data-testid="admin-reset-password-submit">
                          <KeyRound className="h-3 w-3" /> Set Password
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setResetPwOpen(false); setResetPw(''); }}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Permissions (Employee/Office Manager only) */}
                {(editForm.role === 'Employee' || editForm.role === 'Office Manager') && (
                  <>
                    <div className="border-t border-border pt-4">
                      <h3 className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mb-3"><Shield className="h-3 w-3" /> Permissions</h3>
                      <div className="space-y-3">
                        {permToggles.map(p => (
                          <div key={p.key} className="flex items-center justify-between">
                            <Label className="text-sm">{p.label}</Label>
                            <Switch checked={!!perms[p.key]} onCheckedChange={v => updatePerm(p.key, v)} data-testid={`perm-${p.key}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <h3 className="text-xs font-semibold text-muted-foreground mb-3">Visibility</h3>
                      <div className="space-y-3">
                        {visibilityFields.map(f => (
                          <div key={f.key}>
                            <Label className="text-xs text-muted-foreground">{f.label}</Label>
                            <Select value={perms[f.key] || 'assigned'} onValueChange={v => updatePerm(f.key, v)}>
                              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="none">None</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Deactivate / Reactivate Toggle */}
                {selectedUser.id !== user?.id && (
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {selectedUser.is_active !== false ? (
                          <UserCheck className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <UserX className="h-4 w-4 text-destructive" />
                        )}
                        <div>
                          <Label className="text-sm">Account Status</Label>
                          <p className="text-xs text-muted-foreground">
                            {selectedUser.is_active !== false ? 'Active: user can log in' : 'Inactive: login blocked'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedUser.is_active !== false}
                        onCheckedChange={handleToggleActive}
                        disabled={toggling}
                        data-testid="toggle-employee-active"
                      />
                    </div>
                  </div>
                )}

                {/* Delete Employee */}
                {selectedUser.id !== user?.id && (
                  <div className="border-t border-border pt-4">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="w-full gap-1.5"
                      data-testid="delete-employee-button"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete Employee
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Employee</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <span className="font-semibold text-foreground">{selectedUser?.name}</span>? This action cannot be undone and will remove all their account data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} className="border-border" data-testid="delete-employee-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteEmployee} disabled={deleting} data-testid="delete-employee-confirm">
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
